import { __rest } from "tslib";
import React, { useState, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import { getCharAvatar } from '@web-contact/util';
import { useContactAvatar, useContactModel } from '@web-common/hooks/useContactModel';
import { ReactComponent as IconAvatarSelected } from '@/images/icons/account_setting_avatar_selected.svg';
import styles from './avatar.module.scss';
import { useGetProductAuth } from '@web-common/hooks/useGetProductAuth';
const IMG = (props) => {
    const { onError, refreshCount, src } = props, rest = __rest(props, ["onError", "refreshCount", "src"]);
    const [error, setError] = useState(false);
    const originSrc = useRef(src);
    const [realSrc, setRealSrc] = useState(src);
    const timeRef = useRef();
    useEffect(() => {
        error && setRealSrc(originSrc.current);
    }, [refreshCount]);
    useEffect(() => {
        originSrc.current = src;
        setRealSrc(src);
        if (timeRef.current) {
            clearTimeout(timeRef.current);
        }
    }, [src]);
    const loadFn = () => {
        setError(false);
    };
    const onRetry = () => {
        try {
            const imgUrl = new URL(realSrc);
            const _retry = imgUrl.searchParams.get('retry') || 0;
            const retry = +_retry + 1;
            if (retry > 3) {
                onError && onError();
            }
            else {
                imgUrl.searchParams.set('retry', retry + '');
                timeRef.current = window.setTimeout(() => {
                    setRealSrc(imgUrl.href);
                }, retry * 2000);
            }
        }
        catch (e) {
            console.error('[IMG] onRetry error: ', e, 'realSrc: ', realSrc, 'src: ', src, 'props: ', props);
            onError && onError();
        }
    };
    const errorFn = () => {
        setError(true);
        onRetry();
    };
    const style = useMemo(() => {
        return error
            ? Object.assign(Object.assign({}, rest.style), { display: 'none', opacity: '0' }) : rest.style;
    }, [error, rest.style]);
    return React.createElement("img", Object.assign({}, rest, { onLoad: loadFn, onError: errorFn, src: realSrc, style: style }));
};
const BASE_FONT_SIZE = 18;
const BASE_WRAP_SIZE = 32;
const AvatarTag = ({ user, size = '100%', children, className, style, cameraStyle, innerStyle: propInnerStyle, onClick, hasHover, showPendant: showPendantProp = true, contactId, propEmail, showAccountSelected = false, avatarImg, testId = '', showAvatarFirst = true, onLoadImgError, refreshCount, }) => {
    const { productVersionInfo: { navigateVersion }, } = useGetProductAuth();
    const showPendant = !(process.env.BUILD_ISEDM || navigateVersion) && showPendantProp;
    const ref = useRef(null);
    const [elWidth, setElWidth] = useState(BASE_WRAP_SIZE);
    if (!user && !contactId && !propEmail) {
        return React.createElement("div", null);
    }
    const model = useContactModel({ contactId, name: user === null || user === void 0 ? void 0 : user.name, email: propEmail, useCompositeQuery: true });
    const { avatar, avatarTxt, color, avatarPendant } = useContactAvatar({
        email: propEmail,
        contactId,
        emailTxt: user === null || user === void 0 ? void 0 : user.email,
        name: user === null || user === void 0 ? void 0 : user.name,
        color: user === null || user === void 0 ? void 0 : user.color,
        avatar: user === null || user === void 0 ? void 0 : user.avatar,
        logcAvatar: user === null || user === void 0 ? void 0 : user.logcAvatar,
        model,
        showAvatarFirst,
    });
    const showName = avatarImg || getCharAvatar(avatarTxt);
    const innerStyle = useMemo(() => {
        const logcInnerStyle = { fontSize: BASE_FONT_SIZE };
        if (avatar) {
            logcInnerStyle.backgroundColor = '#ffffff';
            logcInnerStyle.width = '100%';
            logcInnerStyle.height = '100%';
        }
        else {
            logcInnerStyle.backgroundColor = color;
            if (size === '100%') {
                const scale = elWidth / BASE_WRAP_SIZE;
                logcInnerStyle.transform = `scale(${scale > 0 ? scale : 1})`;
            }
            else {
                const scale = Number(size) / 32;
                logcInnerStyle.transform = `scale(${!Number.isNaN(scale) && scale > 0 ? scale : 1})`;
            }
        }
        if (avatarTxt.length > 1) {
            logcInnerStyle.fontSize = BASE_FONT_SIZE - 6;
        }
        return logcInnerStyle;
    }, [size, avatar, color, avatarTxt, elWidth]);
    useLayoutEffect(() => {
        if (size === '100%') {
            setElWidth(ref.current ? ref.current.offsetWidth : BASE_WRAP_SIZE);
        }
    }, [size]);
    const clickAvatarTag = (e) => {
        onClick && onClick(e);
    };
    const avatarDom = !avatar ? (showName) : (React.createElement(React.Fragment, null,
        React.createElement(IMG, { onError: () => {
                imgError();
            }, refreshCount: refreshCount, src: avatar, style: { width: '100%', height: '100%', float: 'left' }, alt: showName }),
        showName));
    const [errorInnerStyle, setErrorInnerStyle] = useState({});
    const imgError = () => {
        let transform;
        if (size === '100%') {
            const scale = elWidth / BASE_WRAP_SIZE;
            transform = `scale(${scale > 0 ? scale : 1})`;
        }
        else {
            const scale = Number(size) / 32;
            transform = `scale(${!Number.isNaN(scale) && scale > 0 ? scale : 1})`;
        }
        setErrorInnerStyle({
            width: BASE_WRAP_SIZE + 'px',
            height: BASE_WRAP_SIZE + 'px',
            transform,
            backgroundColor: color,
        });
        onLoadImgError && onLoadImgError();
    };
    return (React.createElement("div", { className: className, style: Object.assign(Object.assign({}, style), { width: size, height: size }), ref: ref, "data-test-id": testId, onClick: clickAvatarTag },
        React.createElement("div", { className: `${styles.avatarTagWrap} ${hasHover ? styles.hasHover : ''}` },
            hasHover && (React.createElement("div", { className: styles.hoverBg },
                React.createElement("div", { className: styles.camera, style: Object.assign({}, cameraStyle) }))),
            React.createElement("div", { className: `${styles.avatarTag} ${showAccountSelected ? styles.avatarTagGreenBorder : ''} avatarTag`, style: Object.assign(Object.assign(Object.assign({}, innerStyle), propInnerStyle), errorInnerStyle) }, children ? React.createElement("div", { className: styles.childrenWrap }, children) : avatarDom),
            showPendant && avatarPendant && React.createElement(IMG, { className: styles.avatorPendant, src: avatarPendant, alt: "" }),
            showAccountSelected && (React.createElement("span", { className: styles.avatarSelectedIcon },
                React.createElement(IconAvatarSelected, null))))));
};
export default AvatarTag;
